/**
 * StatCard.tsx, PageHeader.tsx, EmptyState.tsx, LoadingSkeleton.tsx
 *
 * Composable high-level components used across most pages.
 */

import { ReactNode } from 'react';
import { theme } from '../../theme';

/* ═══════════════ STAT CARD ═══════════════ */
/**
 * KPI card. Use `primary` on at most ONE stat per screen — this gets the glow.
 *
 * Two layout modes:
 *   - inline (default): use inside StatGrid, rendered as a bordered cell
 *   - standalone: adds its own border and background
 */

interface StatCardProps {
  label: ReactNode;
  value: ReactNode;
  unit?: ReactNode;
  delta?: ReactNode;
  deltaType?: 'up' | 'down' | 'neutral';
  primary?: boolean;
  sparkline?: ReactNode;
  icon?: ReactNode;
  standalone?: boolean;
}

export function StatCard({
  label, value, unit, delta, deltaType = 'neutral', primary, sparkline, icon, standalone,
}: StatCardProps) {
  const deltaColor = deltaType === 'up' ? theme.semantic.success
    : deltaType === 'down' ? theme.semantic.danger
    : theme.text.tertiary;

  const bg = primary
    ? `linear-gradient(135deg, ${theme.surface.panel} 0%, rgba(0,212,255,0.04) 100%)`
    : theme.surface.panel;

  return (
    <div style={{
      background: bg,
      padding: `${theme.space[5]} ${theme.space[6]}`,
      display: 'flex',
      flexDirection: 'column',
      gap: theme.space[2],
      position: 'relative',
      overflow: 'hidden',
      // Fill the grid cell vertically so sibling cards in StatGrid stay aligned
      // at top and bottom regardless of which ones have deltas/sparklines.
      height: '100%',
      boxSizing: 'border-box',
      ...(standalone ? {
        border: `1px solid ${theme.borderColor.default}`,
        borderRadius: theme.radius.lg,
      } : {}),
    }}>
      {primary && (
        <div style={{
          position: 'absolute',
          inset: 0,
          borderLeft: `2px solid ${theme.brand.cyan}`,
          boxShadow: `inset 0 0 40px -10px ${theme.brand.cyanGlow}`,
          pointerEvents: 'none',
        }} />
      )}

      <div style={{
        fontSize: theme.fontSize.xs,
        fontWeight: theme.fontWeight.medium,
        letterSpacing: theme.letterSpacing.wide,
        textTransform: 'uppercase',
        color: theme.text.tertiary,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 6,
        // Reserve ≈2 lines so cards with 1-word labels align vertically with
        // cards whose labels wrap. Keeps the value row on the same baseline.
        minHeight: 28,
        lineHeight: 1.3,
      }}>
        {icon}
        <span>{label}</span>
      </div>

      <div style={{
        fontSize: '28px',
        fontWeight: theme.fontWeight.semibold,
        letterSpacing: theme.letterSpacing.tight,
        color: primary ? theme.brand.cyanLight : theme.text.primary,
        fontFamily: theme.font.data,
        fontVariantNumeric: 'tabular-nums',
        lineHeight: 1,
        whiteSpace: 'nowrap',
      }}>
        {value}
        {unit && <span style={{ fontSize: 18, color: theme.text.tertiary }}>{unit}</span>}
      </div>

      {delta ? (
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          fontSize: theme.fontSize.xs,
          fontFamily: theme.font.data,
          color: deltaColor,
          minHeight: 16,
        }}>
          {delta}
        </div>
      ) : (
        // Reserve the delta row height so sibling cards with deltas stay vertically aligned.
        <div aria-hidden style={{ minHeight: 16 }} />
      )}

      {sparkline && (
        <div style={{
          position: 'absolute',
          right: theme.space[4],
          bottom: theme.space[4],
          opacity: 0.7,
        }}>
          {sparkline}
        </div>
      )}
    </div>
  );
}

/**
 * StatGrid — Wrapper for multiple StatCards. Creates a connected horizontal strip
 * with hairline borders between cells. Pass children as StatCards.
 */
export function StatGrid({
  children, columns = 'auto',
}: {
  children: ReactNode;
  columns?: string | number;
}) {
  const cols = typeof columns === 'number'
    ? `repeat(${columns}, 1fr)`
    : columns === 'auto'
    ? `repeat(auto-fit, minmax(180px, 1fr))`
    : columns;

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: cols,
      gap: 1,
      background: theme.borderColor.subtle,
      borderRadius: theme.radius.lg,
      overflow: 'hidden',
      border: `1px solid ${theme.borderColor.default}`,
    }}>
      {children}
    </div>
  );
}

/* ═══════════════ PAGE HEADER ═══════════════ */

interface PageHeaderProps {
  title: ReactNode;
  eyebrow?: ReactNode;
  description?: ReactNode;
  meta?: ReactNode;
  actions?: ReactNode;
  style?: React.CSSProperties;
}

export function PageHeader({ title, eyebrow, description, meta, actions, style }: PageHeaderProps) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: theme.space[4],
      marginBottom: theme.space[8],
      // Reserve ~72px corridor on the right so action buttons never collide
      // with the fixed profile avatar (36px wide at right:20). This is page-local;
      // the AppLayout profile sits at the viewport corner.
      paddingRight: actions ? 72 : 0,
      ...style,
    }}>
      <div style={{ minWidth: 0, flex: 1 }}>
        {eyebrow && (
          <div style={{
            fontSize: theme.fontSize.xs,
            fontWeight: theme.fontWeight.semibold,
            letterSpacing: theme.letterSpacing.wider,
            textTransform: 'uppercase',
            color: theme.brand.cyan,
            marginBottom: 8,
          }}>
            {eyebrow}
          </div>
        )}
        <h1 style={{
          fontSize: theme.fontSize['2xl'],
          fontWeight: theme.fontWeight.semibold,
          letterSpacing: theme.letterSpacing.tightish,
          lineHeight: 1.1,
          margin: 0,
          marginBottom: description || meta ? 6 : 0,
          color: theme.text.primary,
        }}>
          {title}
        </h1>
        {description && (
          <p style={{
            fontSize: theme.fontSize.md,
            color: theme.text.secondary,
            margin: 0,
            maxWidth: 720,
            marginTop: 4,
          }}>
            {description}
          </p>
        )}
        {meta && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: theme.space[3],
            fontSize: theme.fontSize.sm,
            color: theme.text.tertiary,
            marginTop: 8,
            flexWrap: 'wrap',
          }}>
            {meta}
          </div>
        )}
      </div>
      {actions && (
        <div style={{ display: 'flex', alignItems: 'center', gap: theme.space[3], flexWrap: 'wrap' }}>
          {actions}
        </div>
      )}
    </div>
  );
}

/**
 * Live indicator — a pulsing dot + text. Use in PageHeader `meta` slot.
 */
export function LiveDot({ children, color = theme.semantic.success }: { children?: ReactNode; color?: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <style>{`@keyframes meridian-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }`}</style>
      <span style={{
        width: 6, height: 6,
        borderRadius: '50%',
        background: color,
        boxShadow: `0 0 8px ${color}`,
        animation: 'meridian-pulse 2s ease infinite',
        flexShrink: 0,
      }} />
      {children}
    </span>
  );
}

/* ═══════════════ EMPTY STATE ═══════════════ */

interface EmptyStateProps {
  icon?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  compact?: boolean;
}

export function EmptyState({ icon, title, description, action, compact }: EmptyStateProps) {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      padding: compact ? `${theme.space[8]} ${theme.space[6]}` : `${theme.space[16]} ${theme.space[6]}`,
      gap: theme.space[3],
    }}>
      {icon && (
        <div style={{
          width: 44, height: 44,
          borderRadius: '50%',
          background: theme.surface.card,
          border: `1px solid ${theme.borderColor.default}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: theme.text.tertiary,
          marginBottom: theme.space[1],
        }}>
          {icon}
        </div>
      )}
      <div style={{
        fontSize: theme.fontSize.md,
        fontWeight: theme.fontWeight.semibold,
        color: theme.text.primary,
      }}>
        {title}
      </div>
      {description && (
        <div style={{
          fontSize: theme.fontSize.sm,
          color: theme.text.tertiary,
          maxWidth: 360,
          lineHeight: 1.5,
        }}>
          {description}
        </div>
      )}
      {action && <div style={{ marginTop: theme.space[3] }}>{action}</div>}
    </div>
  );
}

/* ═══════════════ LOADING SKELETON ═══════════════ */

export function Skeleton({
  width = '100%',
  height = 14,
  rounded = 4,
  style,
}: {
  width?: string | number;
  height?: string | number;
  rounded?: number;
  style?: React.CSSProperties;
}) {
  return (
    <>
      <style>{`
        @keyframes meridian-skeleton {
          0% { background-position: -200px 0; }
          100% { background-position: calc(200px + 100%) 0; }
        }
      `}</style>
      <div style={{
        width,
        height,
        borderRadius: rounded,
        background: 'linear-gradient(90deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.07) 50%, rgba(255,255,255,0.03) 100%)',
        backgroundSize: '200px 100%',
        backgroundRepeat: 'no-repeat',
        animation: 'meridian-skeleton 1.4s ease infinite',
        ...style,
      }} />
    </>
  );
}

export function SkeletonStack({ count = 3, gap = 12 }: { count?: number; gap?: number }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap }}>
      {Array.from({ length: count }).map((_, i) => (
        <Skeleton key={i} width={`${60 + Math.random() * 35}%`} />
      ))}
    </div>
  );
}
